import { useCallback, useEffect, useRef, useState } from 'react';

const STATUS = { pending: 'ждёт агента', leased: 'агент собирает', completed: 'готово', failed: 'не удалось', needs_user_action: 'нужно действие в Seller' };
const ERRORS = {
  seller_login_required: 'Войдите в Seller в профиле агента и повторите.',
  no_cabinet: 'Ни один из ваших кабинетов не найден в меню Seller этого Chrome.',
  seller_cdp_error: 'Сбой в Chrome агента; повторите сбор.',
};

// «Собрать словарь через агента»: агент по ключу берёт «Аналитика → Поисковые запросы» Ozon и создаёт новый словарь.
// Premium: 28 дней файлом; без Premium: 7 дней по страницам, частотность ×4. Кабинет агент выбирает сам.
export default function AgentDictionaryCollector({ authFetch, base, accountId, onDictionary }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const announced = useRef(new Set());

  const reload = useCallback(async () => {
    try {
      const data = await authFetch(`${base}/${accountId}/semantics/collect`);
      setItems(data.items || []);
      (data.items || []).forEach((item) => {
        if (item.status === 'completed' && item.dictionary && !announced.current.has(item.task_id)) {
          announced.current.add(item.task_id);
          if (onDictionary) onDictionary(item.dictionary);
        }
      });
    } catch (error) {
      // список сборов не критичен для остальной страницы
    }
  }, [authFetch, base, accountId, onDictionary]);

  useEffect(() => { announced.current = new Set(); reload(); }, [accountId]); // eslint-disable-line
  useEffect(() => {
    if (!items.some((item) => item.status === 'pending' || item.status === 'leased')) return undefined;
    const timer = setTimeout(reload, 6000);
    return () => clearTimeout(timer);
  }, [items, reload]);

  const start = async () => {
    const key = query.trim();
    if (key.length < 2) { setMessage('Введите ключ'); return; }
    setBusy(true);
    setMessage('');
    try {
      await authFetch(`${base}/${accountId}/semantics/collect`, { method: 'POST', body: JSON.stringify({ query: key }) });
      setQuery('');
      setMessage('Задача поставлена агенту. Словарь появится в закладках, когда сбор закончится (с Premium это несколько минут, без него дольше).');
      await reload();
    } catch (error) {
      setMessage(error.message || 'Не удалось поставить задачу');
    } finally {
      setBusy(false);
    }
  };

  const shown = items.filter((item) => item.status !== 'completed' || !announced.current.has(item.task_id)).slice(0, 5);

  return (
    <div style={{ padding: '0 16px 12px' }}>
      {!open ? (
        <button type="button" className="autoseo-btn" onClick={() => setOpen(true)}
          title="Агент сам выберет кабинет с Premium, возьмёт запросы за 28 дней по вашему ключу и создаст словарь">
          Собрать словарь через агента
        </button>
      ) : (
        <div className="autoseo-hint" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>
            Агент заходит в «Аналитика → Поисковые запросы», вбивает ключ и берёт запросы с частотностью. Если у любого из ваших кабинетов есть Premium, берётся 28 дней
            (скачивается отчёт). Без Premium доступны только 7 дней: агент соберёт их по страницам и умножит частотность на 4.
            Словарь называется «Ключ, дата, дней, агент».
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="text" className="autoseo-textinput" placeholder="Ключ, например: компрессор автомобильный" value={query} maxLength={300}
              onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') start(); }} style={{ minWidth: 280 }} />
            <button type="button" className="autoseo-btn autoseo-btn--primary" disabled={busy} onClick={start}>Собрать</button>
            <button type="button" className="autoseo-btn" onClick={() => setOpen(false)}>Свернуть</button>
          </div>
          {message && <div>{message}</div>}
        </div>
      )}
      {shown.length > 0 && (
        <div className="autoseo-hint" style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {shown.map((item) => (
            <div key={item.task_id}>
              «{item.query}»: {STATUS[item.status] || item.status}
              {item.status === 'completed' && item.dictionary ? ` — словарь «${item.dictionary.name}», запросов ${item.dictionary.count}` : ''}
              {item.status === 'completed' && !item.dictionary ? ' — запросов по ключу не нашлось' : ''}
              {(item.status === 'failed' || item.status === 'needs_user_action') ? `. ${ERRORS[item.error_code] || item.error_message || ''}` : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
